import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react'
import { RefreshCw, ShieldCheck } from 'lucide-react'
import {
  ServerApiClient,
  type AccessGrantRow,
  type FgCapability,
} from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'

/**
 * Team access — founder-only.
 *
 * Two tiers, and this panel exists to make the second one legible. A founder
 * holds every area by allowlist; a granted teammate holds exactly the boxes
 * checked here and nothing else. The status chip is the part that heads off the
 * support ticket: an invited teammate who has not chosen a password yet cannot
 * sign in at all, so the row says that instead of reading "granted" and leaving
 * the founder to guess why nothing works.
 */

type Props = { topInsetPx?: number }

const CAPABILITIES: FgCapability[] = ['tour_author', 'crm']

const CAPABILITY_LABEL: Record<FgCapability, string> = {
  tour_author: 'Tour authoring',
  crm: 'CRM',
}

const CAPABILITY_NOTE: Record<FgCapability, string> = {
  tour_author: 'Build, publish and measure guided tours.',
  crm: 'Work the lead desk: contacts, timelines, stages, notes.',
}

/** What the server says happened to their login, in words a founder can act on. */
const LOGIN_STATE_MESSAGE: Record<string, string> = {
  invited: "Invited — they'll get an email to set a password",
  pending_setup: 'Already invited; still needs to set a password',
  existing: 'Access added to their existing login',
}

const panel: CSSProperties = {
  border: '1px solid #d9e2df', background: '#fff', borderRadius: 8,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
}
const buttonStyle: CSSProperties = {
  border: '1px solid #c8d7d2', background: '#fff', color: '#15343b', borderRadius: 7,
  padding: '7px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
}
const thStyle: CSSProperties = {
  textAlign: 'left', fontSize: 11, color: '#5d6d72', fontWeight: 800,
  borderBottom: '1px solid #e4ece9', padding: '8px 10px', whiteSpace: 'nowrap',
}
const tdStyle: CSSProperties = {
  fontSize: 12, color: '#223236', borderBottom: '1px solid #eef3f1',
  padding: '8px 10px', verticalAlign: 'top',
}
/** Same button, said in red — a full `border` so toggling never drops a longhand. */
const dangerButtonStyle: CSSProperties = {
  ...buttonStyle, border: '1px solid #e5b4b4', background: '#fdeeee', color: '#8c2f2f',
}
const inputStyle: CSSProperties = {
  font: 'inherit', fontSize: 13, padding: '8px 10px', borderRadius: 7, border: '1px solid #d9e2df',
}
const sectionTitle: CSSProperties = { fontSize: 13, fontWeight: 800, color: '#15343b', margin: '0 0 2px' }
const sectionNote: CSSProperties = { fontSize: 11, color: '#5d6d72', margin: '0 0 10px' }
const smallText: CSSProperties = { fontSize: 12, color: '#5d6d72' }

const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Three states, in the order that decides whether access actually works today:
 * signed-in beats invited beats granted-to-a-stranger.
 */
const statusChip = (row: AccessGrantRow): { label: string; background: string; color: string } => {
  if (row.hasLogin) return { label: 'Active', background: '#dcfce7', color: '#166534' }
  if (row.pendingSetup) return { label: 'Invite sent — awaiting password', background: '#fef9c3', color: '#854d0e' }
  return { label: 'No login yet', background: '#f3f4f6', color: '#5d6d72' }
}

export function FounderAccessPanel({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const [grants, setGrants] = useState<AccessGrantRow[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Grant form.
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [picked, setPicked] = useState<FgCapability[]>([])
  const [invite, setInvite] = useState(true)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  // Revoking is two clicks in place — window.confirm cannot say which grant.
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await ensureApiSessionReady()
      setGrants(await api.listAccessGrants())
      setLoaded(true)
    } catch (err: unknown) {
      setError(String((err as Error)?.message || 'Failed to load access grants.'))
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { void load() }, [load])

  // Sorted by email so one person's capabilities read as a block.
  const rows = useMemo(() => {
    const sorted = [...grants]
    sorted.sort((a, b) =>
      a.email.localeCompare(b.email) ||
      (CAPABILITY_LABEL[a.capability] || a.capability).localeCompare(CAPABILITY_LABEL[b.capability] || b.capability))
    return sorted
  }, [grants])

  const toggleCapability = useCallback((capability: FgCapability) => {
    setPicked((prev) => (prev.includes(capability) ? prev.filter((c) => c !== capability) : [...prev, capability]))
  }, [])

  // Canonical order, not click order: what the founder sends should not depend
  // on which box they happened to tick first.
  const chosen = CAPABILITIES.filter((c) => picked.includes(c))

  const submit = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    const cleanEmail = email.trim()
    if (!cleanEmail || !chosen.length || saving) return
    setSaving(true)
    setError(null)
    setResult(null)
    try {
      const out = await api.grantAccess({
        email: cleanEmail,
        capabilities: chosen,
        ...(note.trim() ? { note: note.trim() } : {}),
        invite,
      })
      const said = LOGIN_STATE_MESSAGE[out.loginState] || 'Access saved'
      setResult(`${out.email || cleanEmail} — ${said}.`)
      setEmail('')
      setNote('')
      setPicked([])
      await load()
    } catch (err: unknown) {
      setError(String((err as Error)?.message || 'Failed to grant access.'))
    } finally {
      setSaving(false)
    }
  }, [api, chosen, email, invite, load, note, saving])

  const revoke = useCallback(async (id: string) => {
    setConfirmId(null)
    setError(null)
    setResult(null)
    try {
      await api.revokeAccessGrant(id)
      await load()
    } catch (err: unknown) {
      setError(String((err as Error)?.message || 'Failed to revoke that grant.'))
    }
  }, [api, load])

  return (
    <div style={{ height: `calc(100vh - ${topInsetPx}px)`, marginTop: topInsetPx, overflowY: 'auto', padding: '14px 16px 40px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, color: '#15343b', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={16} /> Team access
          </h2>
          <span style={{ flex: 1 }} />
          <button style={buttonStyle} onClick={() => void load()} disabled={loading}>
            <RefreshCw size={13} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        <p style={{ ...smallText, margin: 0 }}>
          A teammate you grant access to gets only the areas you check here — nothing else in this console
          opens up. Founders (the FG_FOUNDER_ACTIVITY_EMAILS allowlist) always have everything, grant or no grant,
          and are not listed below.
        </p>

        {error ? (
          <div style={{ ...panel, padding: 12, borderColor: '#e5b4b4', background: '#fdeeee', color: '#8c2f2f', fontSize: 12 }}>{error}</div>
        ) : null}

        {result ? (
          <div style={{ ...panel, padding: 12, borderColor: '#b7e2c3', background: '#f0fdf4', color: '#166534', fontSize: 12 }} data-testid="access-grant-result">
            {result}
          </div>
        ) : null}

        <section style={{ ...panel, padding: 12 }}>
          <h3 style={sectionTitle}>Who has access</h3>
          <p style={sectionNote}>One row per capability. Revoking is recorded, never deleted.</p>
          {rows.length ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Person</th>
                  <th style={thStyle}>Can do</th>
                  <th style={thStyle}>Granted</th>
                  <th style={thStyle}>Sign-in</th>
                  <th style={{ ...thStyle, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const chip = statusChip(row)
                  return (
                    <tr key={row.id} data-testid="access-grant-row">
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700 }}>{row.email}</div>
                        {row.note ? <div style={{ color: '#5d6d72', fontSize: 11 }}>{row.note}</div> : null}
                      </td>
                      <td style={tdStyle}>{CAPABILITY_LABEL[row.capability] || row.capability}</td>
                      <td style={tdStyle}>{fmtDate(row.grantedAt)}</td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: chip.background, color: chip.color }}>
                          {chip.label}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {confirmId === row.id ? (
                          <>
                            <button style={dangerButtonStyle} onClick={() => void revoke(row.id)}>Confirm remove</button>
                            <button style={{ ...buttonStyle, marginLeft: 6 }} onClick={() => setConfirmId(null)}>Keep</button>
                          </>
                        ) : (
                          <button style={buttonStyle} onClick={() => setConfirmId(row.id)}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : loading && !loaded ? (
            <div style={smallText}>Loading access grants…</div>
          ) : (
            <div style={smallText}>
              Nobody outside the founder allowlist has access yet. Grant one of the areas below to change that.
            </div>
          )}
        </section>

        <section style={{ ...panel, padding: 12 }}>
          <h3 style={sectionTitle}>Give someone access</h3>
          <p style={sectionNote}>
            Pick the areas they need. Granting the same area twice is harmless — it stays one grant.
          </p>
          <form data-testid="access-grant-form" onSubmit={(e) => void submit(e)} style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="teammate@example.com"
                aria-label="Email address"
                data-testid="access-grant-email"
                style={{ ...inputStyle, flex: '1 1 260px' }}
              />
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (optional) — why they need it"
                aria-label="Note"
                data-testid="access-grant-note"
                style={{ ...inputStyle, flex: '1 1 260px' }}
              />
            </div>

            <div style={{ display: 'grid', gap: 6 }}>
              {CAPABILITIES.map((capability) => (
                <label key={capability} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#223236' }}>
                  <input
                    type="checkbox"
                    checked={picked.includes(capability)}
                    onChange={() => toggleCapability(capability)}
                    data-testid={`access-capability-${capability}`}
                    aria-label={CAPABILITY_LABEL[capability]}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <b>{CAPABILITY_LABEL[capability]}</b>
                    <span style={{ color: '#5d6d72' }}> — {CAPABILITY_NOTE[capability]}</span>
                  </span>
                </label>
              ))}
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#223236' }}>
              <input
                type="checkbox"
                checked={invite}
                onChange={() => setInvite((prev) => !prev)}
                data-testid="access-grant-invite"
                aria-label="Send them a set-up email"
                style={{ marginTop: 2 }}
              />
              <span>
                <b>Send them a set-up email</b>
                <span style={{ color: '#5d6d72' }}>
                  {' '}— if they have no FuseGrid login yet, they get an email with a link and choose their own
                  password. Nobody here sets or sees it. Leave this off and the grant is saved, but it does
                  nothing until they have a login.
                </span>
              </span>
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="submit" style={buttonStyle} disabled={saving || !email.trim() || !chosen.length}>
                {saving ? 'Granting…' : 'Grant access'}
              </button>
              <span style={smallText}>
                {chosen.length
                  ? `They will be able to: ${chosen.map((c) => CAPABILITY_LABEL[c]).join(' and ')}.`
                  : 'Check at least one area.'}
              </span>
            </div>
          </form>
        </section>
      </div>
    </div>
  )
}
